import { Immutable } from "immer";
import React from "react";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import type { VideoMediaSection as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { originalUrl } from "../../utils/wagtail";

import bannerStyles from "../../styles/composable/banner.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/media_sections/video_media_section.html`` (#33285).
 *
 * - The Wistia popover span + thumb container are transcribed verbatim
 *   (static markup; the global Wistia scanner binds it on load -- no JS
 *   here). The legacy ids ``wistia-{video_id}-1`` and
 *   ``wistia_31.thumb_container`` are preserved exactly.
 * - Unlike MediaSection (which always emits ``banner__content--rtq``), the
 *   ``banner__content`` div here gets ``banner__content--rtq`` ONLY when
 *   ``orientation == 'left'``.
 * - ``description`` is the serialized RichTextBlock string (includes the
 *   ``<div class="rich-text">`` wrapper) -- exact parity with the legacy
 *   ``{{ self.description|richtext }}``.
 */
export const VideoMediaSection = (props: IProps): React.JSX.Element => {
    const staticURL = useStaticURL();
    const videoId = props.value.wistia_video_id ?? "";
    return (
        <section
            className={concatClassNames([
                bannerStyles.banner,
                "ptb",
                props.value.orientation === "right"
                    ? bannerStyles.bannerReverse
                    : null,
            ])}
        >
            <figure className={bannerStyles.bannerFigure}>
                <span
                    className={`wistia_embed wistia_async_${videoId} popover=true popoverContent=link wistia_embed_initialized`}
                    id={`wistia-${videoId}-1`}
                >
                    <div
                        id="wistia_31.thumb_container"
                        className="wistia_click_to_play"
                        style={{
                            position: "relative",
                            height: "70px",
                            width: "70px",
                        }}
                    >
                        <a
                            className="banner__play-btn"
                            href="#"
                            title="Play Video"
                        >
                            <img
                                className="img-responsive"
                                src={`${staticURL}img/media-section/icon-video-play-large.png`}
                                alt="Play Icon"
                                loading="lazy"
                                decoding="async"
                            />
                        </a>
                    </div>
                </span>
                {/* Decorative images have empty alt attr */}
                <img
                    className="img-responsive"
                    src={originalUrl(props.value.image)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
            </figure>

            <div
                className={concatClassNames([
                    bannerStyles.bannerContent,
                    props.value.orientation === "left"
                        ? bannerStyles.bannerContentRtq
                        : null,
                ])}
            >
                <h5 className={bannerStyles.bannerTitle}>
                    {props.value.title}
                </h5>
                <div
                    className={concatClassNames([
                        bannerStyles.bannerCopy,
                        "alt-copy",
                    ])}
                    dangerouslySetInnerHTML={{
                        __html: props.value.description ?? "",
                    }}
                />
            </div>
        </section>
    );
};
